import { HttpContext, HttpEvent, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ApiService, Guid, SKIP_OFFLINE_INTERCEPTOR, UploadDataForAntragsSeite } from '@ebeihilfe/core';
import { ServerResponse } from 'http';
import { Observable } from 'rxjs';

import { AntragFinishDto } from '../dto/antrag-finish.dto';
import { AntragUpdateDto } from '../dto/antrag-update';
import { AnhangDataType, AntragLimit } from '../model';
import { ValidationErrorResponse } from '../model/validation-error-response';

@Injectable({
  providedIn: 'root',
})
export class EinreichungApiService {
  private readonly API_URL = 'data/';
  private readonly api = inject(ApiService);
  private readonly context = new HttpContext().set(SKIP_OFFLINE_INTERCEPTOR, true);

  public finishEinreichung(antragFinishObject: AntragFinishDto, reqOpts?: { context: HttpContext }): Observable<void> {
    return this.api.put(
      `${this.API_URL}antraege/${antragFinishObject.antragsId}`,
      {
        belegeCount: antragFinishObject.numberOfBelege,
        seitenCount: antragFinishObject.numberOfPages,
        antragsArt: antragFinishObject.typeOfAntrag,
        metadaten: antragFinishObject.encryptedMetadata,
        concurrency: antragFinishObject.antragCreationDate,
        flags: antragFinishObject.flags,
        surveyDaten: antragFinishObject.encryptedSurveyDaten,
      },
      reqOpts,
    ) as Observable<void>;
  }

  public deleteAntrag(antragsId: string): Observable<void> {
    return this.api.delete(`${this.API_URL}antraege/${antragsId}`) as Observable<void>;
  }

  /**
   * Fetches the AntragsLimit data from the API.
   *
   * @return {Observable<AntragLimit>} An observable emitting the fetched limit data.
   */
  public getAntragsLimit(): Observable<AntragLimit> {
    return this.api.restGet<AntragLimit>(`${this.API_URL}antraege/info`);
  }

  public uploadAnhang(
    pagesToUpload: UploadDataForAntragsSeite & { dataType?: AnhangDataType },
  ): Observable<ValidationErrorResponse | void> {
    const formData = new FormData();
    formData.append('antragsId', pagesToUpload.antragId);
    formData.append('numberOfBelege', pagesToUpload.belegNumber);
    formData.append('numberOfSeite', pagesToUpload.seiteNumber);
    formData.append('data', pagesToUpload.pageData);
    formData.append('dataType', pagesToUpload.dataType ?? this.extractDataTypeFromDataURI(pagesToUpload.thumb));
    formData.append('thumbnail', pagesToUpload.thumb);
    formData.append('timestamp', pagesToUpload.timestamp);
    formData.append('belegtyp', pagesToUpload.belegtyp);
    // Images and PDFs whose client-side page counting failed have no pageCount - fall back to 1
    formData.append('pageCount', (pagesToUpload.pageCount ?? 1).toString());

    return this.api.post(`${this.API_URL}antraege/${pagesToUpload.antragId}/seiten`, formData, {
      reportProgress: true,
      context: this.context,
    });
  }

  public updateBelegType(antragsId: Guid, seitenId: string, belegType: number): Observable<void> {
    const formData = new FormData();
    formData.append('belegtyp', belegType.toString());

    return this.api.patch(`${this.API_URL}antraege/${antragsId}/seiten/${seitenId}`, formData);
  }

  public updateAntrag(antragUpdateDto: AntragUpdateDto): Observable<{ concurrency: Date }> {
    const body: { concurrency: Date; metadata: string; surveyDaten?: string } = {
      concurrency: antragUpdateDto.concurrency,
      // TODO(BE): the backend still rejects PATCH requests without a `metadata` field, even for
      // surveyDaten-only draft updates that should not carry metadata. Send an empty-string stub
      // until the backend makes `metadata` optional. The empty value is safe on the read path —
      // both web (`decryptApplicationMetadata`) and mobile (`populateAntragFromAntragsInfo`) guard
      // on a truthy metadata before decrypting. Remove this stub once the backend is fixed.
      metadata: antragUpdateDto.encryptedMetadata ?? '',
    };
    if (antragUpdateDto.encryptedSurveyDaten !== undefined) {
      body.surveyDaten = antragUpdateDto.encryptedSurveyDaten;
    }

    return this.api.patch<{ concurrency: Date }>(`${this.API_URL}antraege/${antragUpdateDto.antragId}`, body);
  }

  public deleteAnhang(pageId: string): Observable<HttpEvent<ServerResponse>> {
    return this.api.delete(`${this.API_URL}antraege/seiten/${pageId}`) as Observable<HttpEvent<ServerResponse>>;
  }

  public getSeiteFromServer(antragsId: string, seitenId: string): Observable<Blob> {
    return this.api.restGet(`data/antraege/${antragsId}/seiten/${seitenId}`, {
      responseType: 'blob',
      headers: new HttpHeaders({
        'Cache-control': 'private, max-age=31536000',
      }),
    });
  }

  private extractDataTypeFromDataURI(dataURI: string): string {
    const regex = /^data:image\/([^;]+)/;
    const match = dataURI.match(regex);
    return match ? match[1] : 'png';
  }
}
